import { useCallback, useRef, useSyncExternalStore } from 'react';

type SetValue<S> = (update: S | ((prev: S) => S)) => void;

const dispatchStorageEvent = (
  storage: Storage,
  key: string,
  oldValue: string | null,
  newValue: string | null,
) => {
  window.dispatchEvent(
    new StorageEvent('storage', {
      key,
      oldValue,
      newValue,
      storageArea: storage,
    }),
  );
};

type UseStorageOptions = {
  migrateKey?: boolean;
};

const useStorage = <S>(
  storageKey: string,
  initialData: S,
  storage: Storage,
  options?: UseStorageOptions,
): readonly [S, SetValue<S>] => {
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;

  // Cache the parsed snapshot so getSnapshot returns a stable reference
  // between calls when the underlying raw string hasn't changed — required
  // by useSyncExternalStore to avoid infinite re-renders.
  const cacheRef = useRef<{
    key: string;
    raw: string | null;
    parsed: S;
  } | null>(null);

  // Synchronous key migration: when the key changes, copy the old key's value
  // to the new key if the new key is empty. Doing this during render (rather
  // than in an effect) keeps value consistent across the key transition —
  // otherwise we'd flash initialData between the render and the effect.
  // Set migrateKey: false to opt out (e.g. when the key encodes a scope like
  // org ID and values should not carry across scopes).
  const migrateKey = options?.migrateKey ?? true;
  const previousKeyRef = useRef(storageKey);
  if (previousKeyRef.current !== storageKey) {
    const previousKey = previousKeyRef.current;
    if (migrateKey && storage.getItem(storageKey) === null) {
      const oldRaw = storage.getItem(previousKey);
      if (oldRaw !== null) storage.setItem(storageKey, oldRaw);
    }
    previousKeyRef.current = storageKey;
    cacheRef.current = null;
  }

  const getSnapshot = useCallback((): S => {
    const raw = storage.getItem(storageKey);
    const cache = cacheRef.current;
    if (cache && cache.key === storageKey && cache.raw === raw) {
      return cache.parsed;
    }
    if (raw === null) {
      const parsed = initialDataRef.current;
      cacheRef.current = { key: storageKey, raw, parsed };
      return parsed;
    }
    try {
      const parsed = JSON.parse(raw) as S;
      cacheRef.current = { key: storageKey, raw, parsed };
      return parsed;
    } catch {
      storage.removeItem(storageKey);
      const parsed = initialDataRef.current;
      cacheRef.current = { key: storageKey, raw: null, parsed };
      return parsed;
    }
  }, [storage, storageKey]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handler = (event: StorageEvent) => {
        // Ignore events from a different Storage (localStorage vs sessionStorage).
        if (event.storageArea && event.storageArea !== storage) return;
        if (event.key !== null && event.key !== storageKey) return;
        onStoreChange();
      };
      window.addEventListener('storage', handler);
      return () => window.removeEventListener('storage', handler);
    },
    [storage, storageKey],
  );

  const value = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => initialDataRef.current,
  );

  const setValue = useCallback<SetValue<S>>(
    update => {
      const prev = getSnapshot();
      const next =
        typeof update === 'function' ? (update as (p: S) => S)(prev) : update;
      if (Object.is(prev, next)) return;
      const prevRaw = storage.getItem(storageKey);
      const nextRaw = JSON.stringify(next);
      if (prevRaw === nextRaw) return;
      storage.setItem(storageKey, nextRaw);
      dispatchStorageEvent(storage, storageKey, prevRaw, nextRaw);
    },
    [storage, storageKey, getSnapshot],
  );

  return [value, setValue] as const;
};

export const useLocalStorage = <S>(
  storageKey: string,
  initialData: S,
  options?: UseStorageOptions,
) => useStorage(storageKey, initialData, window.localStorage, options);

export const useSessionStorage = <S>(
  storageKey: string,
  initialData: S,
  options?: UseStorageOptions,
) => useStorage(storageKey, initialData, window.sessionStorage, options);
